import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import baseUrl from "../../../../api/baseUrl";
import { inputKeyboardName } from "../../../../store/pos/posAction";
import {
  addItem,
  clearCustomer,
  resetPos,
  selectCustomer,
  setSaleType,
  setSaleDate,
  setCommonDiscountType,
  posChange,
} from "../../../../store/pos/posSlice";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { RootState } from "../../../../store/store";
import { getCurrencySymbol } from "../../../../utils/currency/currencyUtils";

function transformToNewData(existingItem: any, catalogItem?: any) {
  return {
    discount: Number(existingItem.lineDiscount || existingItem.discount || 0),
    discountHistoryId: existingItem.discountHistoryId,
    hasDiscount:
      Number(existingItem.lineDiscount || existingItem.discount || 0) > 0,
    itemId: existingItem.itemId,
    name:
      catalogItem?.name ||
      existingItem.Item?.name ||
      existingItem.itemName ||
      existingItem.name ||
      "Unknown Item",
    itemNumber:
      catalogItem?.itemNumber ||
      existingItem.Item?.itemNumber ||
      existingItem.itemNumber ||
      "",
    barcode:
      catalogItem?.barcode ||
      existingItem.Item?.barcode ||
      existingItem.barcode ||
      "",
    scale:
      catalogItem?.scale ||
      existingItem.Item?.scale ||
      existingItem.scale ||
      "Unit",
    image:
      catalogItem?.image ||
      existingItem.Item?.image ||
      existingItem.image ||
      "",
    stock:
      typeof catalogItem?.stock === "number"
        ? catalogItem.stock
        : Number(catalogItem?.stock || 0),
    modified:
      typeof catalogItem?.modified === "boolean" ? catalogItem.modified : true,
    price: Number(existingItem.price),
    priceHistoryId: existingItem.priceHistoryId,
    quantity: Number(existingItem.quantity),
    tax: Number(existingItem?.tax || 0),
    taxHistoryId: existingItem.taxHistoryId,
  };
}

const RecentBillModel = ({
  isCloseMain,
  isOpen,
  onEditBill,
}: {
  isCloseMain: () => void;
  isOpen: boolean;
  onEditBill?: (billNumber: string | null) => void;
}) => {
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [bills, setBills] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const dispatch = useDispatch();
  const allItems = useSelector((state: RootState) => state.pos.allItems || []);
  const currencySymbol = getCurrencySymbol();

  const formatMoney = (value: any) => {
    const numeric = Number(value || 0);
    return Number.isNaN(numeric) ? "0.00" : numeric.toFixed(2);
  };

  const toNumberValue = (value: any) => {
    const numeric = Number(value || 0);
    return Number.isNaN(numeric) ? 0 : numeric;
  };

  const formatDateTime = (value: any) => {
    if (!value) return "-";
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return "-";
    return dt.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleClose = () => {
    dispatch(inputKeyboardName({ name: "" }));
    isCloseMain();
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const fetchBills = async () => {
      setIsLoading(true);
      setErrorMessage("");
      try {
        const { data } = await baseUrl.get("branch/pos/get-total-sales-last");
        setBills(data.data || []);
      } catch (error) {
        setErrorMessage(
          "Unable to load recent bills. Please check POS API route.",
        );
        console.error("Error fetching bills:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchBills();
  }, [isOpen]);

  const handleEditBill = useCallback(
    async (bill: any) => {
      setSelectedCustomer(bill);

      try {
        const { data } = await baseUrl.get(
          `branch/pos/get-sales-by-bill/${bill.id}`,
        );
        const fetchedSalesData = data.data || [];

        if (!fetchedSalesData.length) {
          return;
        }

        dispatch(resetPos());

        if (bill.customerId) {
          dispatch(
            selectCustomer({
              id: bill.customerId,
              name:
                bill.Customer?.name || bill.customerName || "Unknown Customer",
              phoneNumber:
                bill.Customer?.phoneNumber || bill.customerPhone || "",
              customerType:
                bill.Customer?.customerType || bill.customerType || "normal",
            }),
          );
        } else {
          dispatch(clearCustomer());
        }

        dispatch(setSaleType(bill.creditSale ? "credit" : "cash"));

        const newItems = fetchedSalesData.map((saleRow: any) => {
          const catalogItem = allItems.find(
            (catalog: any) => String(catalog.id) === String(saleRow.itemId),
          );
          return transformToNewData(saleRow, catalogItem);
        });
        newItems.forEach((item: any) => {
          dispatch(addItem(item));
        });

        dispatch(
          posChange({
            name: "billNumber",
            value: bill.billNumber || "",
          }),
        );
        dispatch(posChange({ name: "cash", value: toNumberValue(bill.cash) }));
        dispatch(posChange({ name: "card", value: toNumberValue(bill.card) }));
        dispatch(posChange({ name: "bank", value: toNumberValue(bill.bank) }));
        dispatch(setCommonDiscountType("amount"));
        dispatch(
          posChange({
            name: "commonDiscount",
            value: toNumberValue(bill.givenDiscount || bill.discount),
          }),
        );

        dispatch(
          setSaleDate(
            bill.saleEntryTime ||
              bill.createdAt ||
              fetchedSalesData[0]?.createdAt ||
              null,
          ),
        );

        onEditBill?.(bill.billNumber || null);
        isCloseMain();
      } catch (error) {
        console.error("Error fetching sales data:", error);
      }
    },
    [dispatch, isCloseMain, onEditBill, allItems],
  );

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-erp-navy/55 p-3 sm:p-5 backdrop-blur-[3px]"
      onClick={handleOverlayClick}
    >
      <div
        className="flex h-[min(88vh,820px)] w-full max-w-[1120px] flex-col overflow-hidden rounded-2xl border border-erp-line bg-white font-inter shadow-[0_24px_60px_rgba(11,28,44,0.28)]"
        role="dialog"
        aria-modal="true"
        aria-label="Recent Bills"
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 bg-gradient-to-r from-erp-navy via-erp-navy-mid to-erp-navy-soft px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-erp-gold/40 bg-erp-gold/20 text-erp-gold">
              <ReceiptLongIcon className="!text-[20px]" />
            </div>
            <div className="min-w-0">
              <h2 className="m-0 text-[16px] font-bold tracking-wide text-white">
                Recent Bills
              </h2>
              <p className="m-0 mt-0.5 text-[11px] font-medium text-white/60">
                Review and reopen a recent sale for editing
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="inline-flex rounded-lg border border-white/15 bg-white/10 p-1.5 text-white/85 transition-colors hover:bg-white/20 hover:text-white"
          >
            <CloseIcon className="!text-[18px]" />
          </button>
        </div>

        {/* Body */}
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-4 sm:p-5">
          <div className="flex shrink-0 items-center justify-between gap-2">
            <div className="inline-flex items-center gap-2 rounded-[10px] border border-erp-line bg-erp-accent/10 px-3 py-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.05em] text-erp-muted">
                Total Bills
              </span>
              <span className="text-[14px] font-extrabold tabular-nums text-erp-ink">
                {bills.length}
              </span>
            </div>
          </div>

          {errorMessage ? (
            <div className="shrink-0 rounded-lg border border-erp-danger/25 bg-erp-danger/10 px-3 py-2.5 text-[13px] font-semibold text-erp-danger">
              {errorMessage}
            </div>
          ) : null}

          <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-erp-line bg-white [scrollbar-width:thin]">
            {isLoading ? (
              <div className="flex h-full min-h-[240px] items-center justify-center text-[13px] font-semibold text-erp-muted">
                Loading recent bills...
              </div>
            ) : bills?.length ? (
              <table className="w-full min-w-[860px] border-collapse text-left">
                <thead className="sticky top-0 z-[1]">
                  <tr className="border-b border-erp-line bg-erp-surface text-[11px] font-bold uppercase tracking-[0.04em] text-erp-muted">
                    <th className="px-3 py-2.5 font-bold">Bill No</th>
                    <th className="px-3 py-2.5 text-right font-bold">Amount</th>
                    <th className="px-3 py-2.5 text-right font-bold">
                      Net Total
                    </th>
                    <th className="px-3 py-2.5 font-bold">Customer</th>
                    <th className="px-3 py-2.5 font-bold">Date</th>
                    <th className="px-3 py-2.5 text-center font-bold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {bills.map((bill: any) => {
                    const selected = selectedCustomer?.id === bill.id;
                    return (
                      <tr
                        key={bill.id}
                        onClick={() => setSelectedCustomer(bill)}
                        className={[
                          "cursor-pointer border-b border-erp-line transition-colors last:border-b-0",
                          selected
                            ? "bg-erp-accent/10 shadow-[inset_3px_0_0_#1f6f8b]"
                            : "hover:bg-erp-surface/80",
                        ].join(" ")}
                      >
                        <td className="px-3 py-3 text-[13px] font-bold text-erp-ink">
                          {bill.billNumber || "-"}
                        </td>
                        <td className="px-3 py-3 text-right text-[13px] font-semibold tabular-nums text-erp-ink">
                          {currencySymbol} {formatMoney(bill.amountRecived)}
                        </td>
                        <td className="px-3 py-3 text-right">
                          <span className="inline-flex min-w-[96px] justify-end rounded-lg border border-erp-line bg-erp-accent/10 px-2.5 py-1 text-[13px] font-bold tabular-nums text-erp-accent">
                            {currencySymbol} {formatMoney(bill.netTotal)}
                          </span>
                        </td>
                        <td className="max-w-[180px] truncate px-3 py-3 text-[13px] font-semibold text-erp-ink">
                          {bill.customerId
                            ? bill.Customer?.name ||
                              bill.customerName ||
                              "Unknown"
                            : "Walk-in"}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-[12px] font-medium text-erp-muted">
                          {formatDateTime(bill.createdAt)}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <button
                            type="button"
                            className="inline-flex h-8 min-w-[100px] items-center justify-center rounded-lg bg-erp-navy px-3 text-[11px] font-bold uppercase tracking-[0.04em] text-white transition-colors hover:bg-erp-navy-mid"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditBill(bill);
                            }}
                          >
                            Edit Bill
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="flex h-full min-h-[240px] items-center justify-center text-[13px] font-semibold text-erp-muted">
                No recent bills available
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default RecentBillModel;
